/**
 * Pantalla bloqueante de actualización obligatoria
 * Sprint 6: Se muestra cuando la versión local < versión mínima
 *
 * - No tiene botón de cerrar ni forma de saltar
 * - Touch target mínimo 48px para el botón
 * - Responsive y mobile-first
 */

import { useVersionStore } from '../../stores/versionStore';
import { Capacitor } from '@capacitor/core';
import { Button } from '../../components/ui';

/**
 * Abrir URL de descarga en navegador externo (Capacitor) o nueva pestaña (web)
 */
async function openDownloadUrl(url: string): Promise<void> {
    if (!url.startsWith('https://')) {
        console.error('[UpdateRequired] URL de descarga no es HTTPS:', url);
        return;
    }

    if (Capacitor.isNativePlatform()) {
        // Capacitor: abrir en navegador del sistema
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url });
    } else {
        window.open(url, '_blank', 'noopener');
    }
}

export function UpdateRequiredScreen() {
    const { localVersion, minimumVersion, downloadUrl } = useVersionStore();

    const handleDownload = async () => {
        if (!downloadUrl) return;

        try {
            await openDownloadUrl(downloadUrl);
        } catch (error) {
            console.error('[UpdateRequired] Error al abrir URL:', error);
        }
    };

    return (
        <div className="flex min-h-dvh items-center justify-center bg-background px-5" style={{ padding: 'var(--spacing-safe-area)' }} role="alert">
            <div className="w-full max-w-[380px] text-center">
                {/* Icon */}
                <div className="mb-6 flex justify-center text-primary" aria-hidden="true">
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                </div>

                <h1 className="mb-2 text-xl font-bold text-text">Actualización Requerida</h1>
                <p className="mb-6 text-sm text-text-muted">
                    Necesitas actualizar a la última versión para continuar.
                </p>

                {/* Version info */}
                <div className="mb-6 rounded-xl border border-border bg-surface p-4">
                    <div className="flex items-center justify-between py-1">
                        <span className="text-xs text-text-muted">Versión actual</span>
                        <span className="text-xs font-semibold text-text">{localVersion}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                        <span className="text-xs text-text-muted">Requerida</span>
                        <span className="text-xs font-semibold text-primary">{minimumVersion}</span>
                    </div>
                </div>

                {downloadUrl ? (
                    <Button variant="primary" fullWidth size="large" onClick={handleDownload}>
                        Descargar Actualización
                    </Button>
                ) : (
                    <p className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs text-text-secondary">
                        Descarga no disponible. Contacta al administrador.
                    </p>
                )}

                <p className="mt-4 text-xs text-text-muted">
                    Después de instalar, vuelve a abrir la aplicación.
                </p>
            </div>
        </div>
    );
}
